import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const updated = "August 2026";

  const sections = [
    {
      h: "1. Business Purpose",
      p: "Renovita Research LLC supplies research-grade peptides, reference compounds, and laboratory materials intended exclusively for legitimate scientific, analytical, and educational research purposes. All products offered are provided as research reference materials and are not intended for personal, consumer, or medical use.",
    },
    {
      h: "2. Research Use Only",
      p: "By accessing this website and/or purchasing any products, the buyer acknowledges and agrees that all products sold by Renovita Research LLC are intended strictly for research use only. Products are not intended for human or animal consumption, injection, ingestion, diagnostic, or therapeutic application. All products must be handled and used only by qualified individuals in appropriate research or laboratory settings.",
    },
    {
      h: "3. Buyer Responsibility and Acknowledgment",
      p: "By placing an order, the buyer represents and warrants that:",
      bullets: [
        "They are purchasing products for legitimate research, laboratory, or analytical purposes only",
        "They understand the nature of the materials being purchased",
        "They will use all products in accordance with applicable laws, regulations, and industry standards",
        "They will not misuse any product in a manner inconsistent with its intended research purpose",
      ],
      after: "The buyer assumes all responsibility for the proper handling, storage, and use of any products purchased.",
    },
    {
      h: "4. Prohibited Uses",
      p: "The buyer agrees not to use any product for:",
      bullets: [
        "Human or animal consumption",
        "Injection or bodily introduction of any kind",
        "Diagnostic or therapeutic purposes",
        "Personal or consumer use",
      ],
      after: "Any misuse of products in violation of these Terms may result in order cancellation, refusal of service, and/or account suspension.",
    },
    {
      h: "5. No Medical or Therapeutic Claims",
      p: "Renovita Research LLC makes no representations or warranties regarding the therapeutic, diagnostic, or medicinal value of any product. All products are supplied solely as research reference materials. Any descriptions provided are for informational and scientific reference purposes only and do not constitute medical or health claims.",
    },
    {
      h: "6. Limitation of Liability",
      p: "Renovita Research LLC shall not be held liable for any damages, losses, or consequences arising from the misuse, improper handling, or unauthorized application of any products sold. The buyer assumes full responsibility for compliance with all applicable laws and regulations regarding the purchase, possession, and use of these materials.",
    },
    {
      h: "7. Payment (Zelle)",
      p: "Orders are placed as awaiting-payment. Payment must be sent by Zelle to the recipient shown on the payment instructions page, using the assigned order number as the memo. Orders are confirmed and shipped only after payment is received and verified. Flat-rate shipping of $13.00 is added to every order — free on orders of $200 or more (based on subtotal before discount).",
    },
    {
      h: "8. Shipping, Returns & Privacy",
      p: "Shipping estimates are provided in good faith and are not guaranteed; title and risk of loss pass to the buyer upon delivery to the carrier. International customers are responsible for any duties, taxes, or import compliance. Due to the nature of these materials, all sales are final; damaged or incorrect shipments must be reported within 48 hours of delivery with photos to coastchems@gmail.com. We collect only the information required to fulfill and communicate about your order (name, email, shipping address, order details) and do not sell your data.",
    },
    {
      h: "9. Agreement to Terms",
      p: "By using this website and/or placing an order, you acknowledge that you have read, understood, and agreed to these Terms and Conditions in full.",
    },
  ];

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="terms-page">
      <div className="mx-auto max-w-[900px] px-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-2 label text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="terms-back">
          <ArrowLeft size={16} strokeWidth={1.5} /> Back
        </Link>
        <FadeUp>
          <p className="label text-accent mb-6">/ Terms & Conditions</p>
          <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">The fine print.</h1>
          <p className="mt-6 text-xs font-mono text-muted-foreground">Renovita Research LLC · Last updated: {updated}</p>
        </FadeUp>

        <div className="mt-16 space-y-10">
          {sections.map((s) => (
            <FadeUp key={s.h}>
              <h2 className="font-display text-2xl md:text-3xl font-light leading-tight">{s.h}</h2>
              <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{s.p}</p>
              {s.bullets && (
                <ul className="mt-3 space-y-2 text-sm text-foreground/80 pl-5 list-disc marker:text-accent">
                  {s.bullets.map((b) => (<li key={b}>{b}</li>))}
                </ul>
              )}
              {s.after && <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{s.after}</p>}
            </FadeUp>
          ))}
        </div>

        <div className="mt-16 border-t border-border pt-8 text-sm text-muted-foreground">
          Questions? Contact <a href="mailto:coastchems@gmail.com" className="text-accent border-b border-accent">coastchems@gmail.com</a>.
        </div>
      </div>
    </main>
  );
}
